import {announce} from '@github-ui/aria-live'
import type {TaskComposerProps} from '@github-ui/one-copilot-composer/shared'
import {isSandboxOptionAvailable} from '@github-ui/one-copilot-composer/use-environment-selection'
import type {AgentTasksAppPayload} from '@github-ui/agent-sessions/types/agent-tasks-app-payload'
import type {EnvironmentChatSubmissionHandoff} from '@github-ui/agent-sessions/hooks/use-environment-task-submit'
import {mapMcpPermissionModeToAgentMode} from '@github-ui/agent-sessions/utils/mcp-permission-mode'
import {
  copilotLocalStorage,
  type PersistedTaskRepositorySelection,
} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import {createAhpChatStartRequestWithProvisioningTarget} from '@github-ui/ahp-session-client/contracts'
import {
  ONE_COPILOT_ENTRY_FAILURE_MESSAGES,
  ONE_COPILOT_ENTRY_QUERY_PARAM,
  claimOneCopilotEntry,
  clearOneCopilotEntry,
  readOneCopilotEntryId,
  type OneCopilotEntryRequest,
} from '@github-ui/ahp-session-client/one-copilot-entry'
import {
  readStoredMode,
  useNewSessionApprovalMode,
  writeStoredMode,
  type NewSessionChatLaunch,
  type NewSessionConfiguration,
  type NewSessionInitialInput,
  type NewSessionTarget,
} from '@github-ui/one-copilot-composer/new'
import {DEFAULT_AGENT_MODE, type AgentMode} from '@github-ui/copilot-shared-ui/utils/agent-mode'
import {isFeatureEnabled, isFeatureEnabledWithOptOut} from '@github-ui/feature-flags'
import {useLocation} from '@github-ui/react-router'
import {useSearchParams} from '@github-ui/use-navigate'
import {useCallback, useEffect, useMemo, useRef, useState, type RefObject} from 'react'

import {usePersistentAgentsChat, type LaunchSubmissionFailure} from './PersistentAgentsChatHost'
import {EarlyTaskProvisioning} from '@github-ui/agent-sessions/ahp/early-task-provisioning'
import {isAgentsChatCreationEnabled, isQuickChatEntryPointEnabled} from './use-agents-chats'
import {isQuickChatTaskViewEnabled} from '../utils/is-quick-chat-task'

/**
 * The destination composer cannot represent the stored request, so the request
 * is consumed and reported rather than dropped into a draft that would silently
 * lose its prompt, attachments, or Space.
 */
export const ONE_COPILOT_ENTRY_DESTINATION_UNAVAILABLE_MESSAGE =
  'Copilot could not open this request here. Try starting it again from Copilot.'

/** The request asked for Chat on a page where Chat cannot be started. */
export const ONE_COPILOT_ENTRY_CHAT_UNAVAILABLE_MESSAGE =
  'Copilot Chat is unavailable here, so this request could not be opened. Try starting it again from Copilot.'

export interface AgentsHomeComposerProps {
  earlyProvisioning?: EarlyTaskProvisioning
  /**
   * Remount identity. It changes whenever the mount-scoped `initialInput` does,
   * because the composer snapshots that input on mount and would otherwise keep
   * showing the draft it was first mounted with.
   */
  key: string
  initialInput?: NewSessionInitialInput
  chatLaunch?: NewSessionChatLaunch
}

export interface AgentsHomeChatMode {
  /** Undefined while the mode picker rollout is off, so the host omits the control. */
  selectedMode?: AgentMode
  onSelectMode: (mode: AgentMode) => void
  selectedApprovalMode?: ApprovalMode
  onSelectApprovalMode: (mode: ApprovalMode) => void
}

export interface AgentsHomeChatState {
  /** Which composer the page renders. Owned here so entry handling can never disagree with it. */
  usesOneCopilotComposer: boolean
  composer: AgentsHomeComposerProps
  /** Chat wiring for the composer still in use while the migration rolls out. */
  legacy: Pick<TaskComposerProps, 'newChatPresetId' | 'chatLaunch'>
  /**
   * A stored request is being claimed.
   */
  isClaimingEntry: boolean
  /** The composer remains withheld until an unavailable request is dismissed. */
  isComposerWithheld: boolean
  /** The agent mode the Chat draft will provision with, and the control for it. */
  mode: AgentsHomeChatMode
  entryFailure?: string
  dismissEntryFailure: () => void
  submissionFailure: LaunchSubmissionFailure | undefined
  recoveryPrompt: string | undefined
  clearSubmissionFailure: () => void
  chatSubmissionHandoff: EnvironmentChatSubmissionHandoff | undefined
  /** Shows repository submissions in the provisional task view. Undefined keeps the pre-flag flow. */
  repositorySubmissionHandoff: EnvironmentChatSubmissionHandoff | undefined
  disableSandboxSubmission: boolean
  chatProviderError: Error | undefined
}

type OneCopilotChatTarget = Extract<OneCopilotEntryRequest['target'], {kind: 'chat'}>
type OneCopilotTaskTarget = Extract<OneCopilotEntryRequest['target'], {kind: 'task'}>
type NewSessionTaskTarget = Extract<NewSessionTarget, {kind: 'task'}>
type ApprovalMode = NonNullable<NewSessionConfiguration['approvalMode']>

interface ComposerLaunch {
  key: string
  input?: NewSessionInitialInput
  recoveryInstanceKey?: string
  /** An entry marker was present on the first render and has not resolved yet. */
  pending: boolean
  /** The draft came from a claimed One Copilot request rather than from the URL. */
  fromEntry: boolean
  /** The initial repository came from the temporary embedded-chat handoff. */
  repositoryHandoff?: boolean
  /** An unavailable claimed request must be acknowledged before mounting a draft. */
  withholdComposer?: boolean
}

const HOME_LAUNCH: ComposerLaunch = {
  key: 'agents-home',
  input: {
    target: {kind: 'chat'},
    initialEnvironment: 'sandbox',
    behavior: 'prefill',
  },
  pending: false,
  fromEntry: false,
}

function toChatTarget(target: OneCopilotChatTarget): NewSessionTarget {
  return {
    kind: 'chat',
    ...(target.provisioningTarget ? {provisioningTarget: target.provisioningTarget} : {}),
  }
}

function toTaskTarget(target: OneCopilotTaskTarget): NewSessionTaskTarget {
  const taskTarget: NewSessionTaskTarget = {kind: 'task'}
  const repositoryName = target.repository?.nameWithOwner ?? target.reference?.repository

  if (repositoryName) {
    taskTarget.repository = {kind: 'name', nameWithOwner: repositoryName}
    if (target.repository?.branch) {
      taskTarget.repository.branch = target.repository.branch
    }
  }

  if (target.reference) {
    taskTarget.reference = {path: target.reference.path}
    if (target.reference.range) {
      taskTarget.reference.range = target.reference.range
    }
  }

  return taskTarget
}

function toTarget(target: OneCopilotEntryRequest['target']): NewSessionTarget {
  return target.kind === 'chat' ? toChatTarget(target) : toTaskTarget(target)
}

function toInitialInput(request: OneCopilotEntryRequest): NewSessionInitialInput {
  const target = toTarget(request.target)
  const targetWithSpace: NewSessionTarget =
    target.kind === 'chat' && request.initialSelectedSpace
      ? {...target, context: {kind: 'space', space: request.initialSelectedSpace}}
      : target
  // The cloud task path accepts text and a file/snippet reference, but not the
  // richer AHP attachments carried by a page-context handoff. Keep those task
  // entries on Sandbox so their context remains visible, removable, and
  // submittable instead of silently disappearing on the cloud task path.
  const requiresAttachmentComposer = Boolean(request.prompt.attachments?.length)
  const environment =
    targetWithSpace.kind === 'chat' || requiresAttachmentComposer ? {initialEnvironment: 'sandbox' as const} : {}

  if (request.behavior === 'submit') {
    return {
      target: targetWithSpace,
      ...environment,
      behavior: 'submit',
      id: request.inputId,
      prompt: request.prompt,
      ...(request.directTurnTelemetrySeed ? {directTurnTelemetrySeed: request.directTurnTelemetrySeed} : {}),
    }
  }

  return {target: targetWithSpace, ...environment, behavior: 'prefill', prompt: request.prompt}
}

function createNewChatLaunch(key: string, prompt: string | null, recoveryInstanceKey?: string): ComposerLaunch {
  const input: NewSessionInitialInput = {
    target: {kind: 'chat'},
    initialEnvironment: 'sandbox',
    behavior: 'prefill',
  }
  if (prompt) input.prompt = {text: prompt}

  return {key, input, recoveryInstanceKey, pending: false, fromEntry: false}
}

function createRecoveryChatLaunch(
  input: NewSessionInitialInput,
  recoveryInstanceKey: string | undefined,
): ComposerLaunch {
  return {
    key: 'agents-chat-recovery',
    input,
    recoveryInstanceKey,
    pending: false,
    fromEntry: false,
  }
}

function createRepositoryLaunch(
  key: string,
  repository: string,
  branch?: string,
  repositoryHandoff = false,
): ComposerLaunch {
  return {
    key,
    input: {
      target: {
        kind: 'task',
        repository: {kind: 'name', nameWithOwner: repository, ...(branch ? {branch} : {})},
      },
      // A sidebar or `?repo=` entry keeps the user on their last-selected
      // environment instead of snapping back to Sandbox on every remount.
      restoreLastEnvironment: true,
      behavior: 'prefill',
    },
    pending: false,
    fromEntry: false,
    repositoryHandoff,
  }
}

function getInitialLaunch(
  searchParams: URLSearchParams,
  locationKey: string,
  usesOneCopilotComposer: boolean,
  pendingRepository: PersistedTaskRepositorySelection | null,
): ComposerLaunch {
  // A marker only withholds the composer when this page can actually apply the
  // request; otherwise the effect consumes it and the ordinary draft renders.
  if (usesOneCopilotComposer && readOneCopilotEntryId(searchParams)) {
    return {key: 'agents-entry-pending', pending: true, fromEntry: true}
  }
  if (searchParams.get('chat') === 'new') {
    return createNewChatLaunch(locationKey, searchParams.get('prompt'))
  }
  const repository = searchParams.get('repo')
  if (repository) {
    const branch = searchParams.get('branch') ?? undefined
    return createRepositoryLaunch(locationKey, repository, branch)
  }
  if (pendingRepository) {
    return createRepositoryLaunch(locationKey, pendingRepository.nameWithOwner, pendingRepository.branch, true)
  }
  return HOME_LAUNCH
}

/**
 * Whether the draft the composer will mount with already names a repository.
 *
 * A claimed request decides this for itself rather than deferring to the address
 * bar: the entry marker is removed as soon as it is claimed, so reading `?repo=`
 * afterwards would let a leftover parameter disable a Chat request that had
 * already been accepted. Every other draft still reads the URL, so `?repo=`
 * keeps its ordinary meaning.
 */
function launchSpecifiesRepository(launch: ComposerLaunch, searchParams: URLSearchParams): boolean {
  // A request is still in flight; its own target decides, never the URL.
  if (launch.pending) return false
  if (!launch.fromEntry) return Boolean(searchParams.get('repo'))

  const target = launch.input?.target
  return target?.kind === 'task' && Boolean(target.repository)
}

export function useAgentsHomeChat({
  payload,
  textAreaRef,
}: {
  payload: AgentTasksAppPayload
  /** Focus lands back here after the failure banner is dismissed. */
  textAreaRef?: RefObject<HTMLTextAreaElement | null>
}): AgentsHomeChatState {
  const {
    startLiveChat,
    resolveLiveChatTask,
    failLiveChat,
    submissionFailure,
    recoveryInput,
    recoveryPrompt,
    recoveryInstanceKey,
    settlement,
    clearSubmissionFailure,
    disableSandboxSubmission,
    chatProviderError,
  } = usePersistentAgentsChat()
  const recoveryLaunchPrompt = recoveryPrompt ?? submissionFailure?.text
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const oneCopilotEntryId = readOneCopilotEntryId(searchParams)
  const hasNewChatIntent = searchParams.get('chat') === 'new'
  const hasNewSessionIntent = searchParams.get('new_session') === '1'

  // One decision, read by the page and by entry handling alike, so the marker
  // can never be consumed by a page that is rendering the other composer.
  const usesOneCopilotComposer = isFeatureEnabledWithOptOut('one_copilot_chat', 'one_copilot_chat_ui_opt_out')

  const [pendingRepositorySelection] = useState(() => copilotLocalStorage.getTaskRepositorySelection())
  const [launch, setLaunch] = useState<ComposerLaunch>(() =>
    getInitialLaunch(searchParams, location.key, usesOneCopilotComposer, pendingRepositorySelection),
  )
  const [earlyAttempt, setEarlyAttempt] = useState(() => ({
    launchKey: launch.key,
    provisioning: new EarlyTaskProvisioning(),
  }))
  if (earlyAttempt.launchKey !== launch.key) {
    setEarlyAttempt({launchKey: launch.key, provisioning: new EarlyTaskProvisioning()})
  }
  const earlyProvisioning = earlyAttempt.provisioning
  useEffect(() => () => earlyProvisioning.onDispose(), [earlyProvisioning])
  useEffect(() => {
    if (launch.repositoryHandoff) copilotLocalStorage.setTaskRepositorySelection(null)
  }, [launch.repositoryHandoff])
  const pendingRepositoryFocusKeyRef = useRef<string | undefined>(undefined)
  const [entryFailure, setEntryFailure] = useState<string>()
  const claimedEntryRef = useRef<string | undefined>(undefined)

  if (recoveryInput && (launch.input !== recoveryInput || launch.recoveryInstanceKey !== recoveryInstanceKey)) {
    setLaunch(createRecoveryChatLaunch(recoveryInput, recoveryInstanceKey))
  } else if (
    recoveryLaunchPrompt &&
    !recoveryInput &&
    (launch.input?.target.kind !== 'chat' ||
      launch.input.behavior !== 'prefill' ||
      launch.input.prompt?.text !== recoveryLaunchPrompt ||
      launch.recoveryInstanceKey !== recoveryInstanceKey)
  ) {
    setLaunch(createNewChatLaunch('agents-chat-recovery', recoveryLaunchPrompt, recoveryInstanceKey))
  }

  useEffect(() => {
    if (!settlement) return
    const frame = requestAnimationFrame(() => {
      setLaunch(current => {
        if (
          current.key !== 'agents-chat-recovery' ||
          current.recoveryInstanceKey !== settlement.instanceKey ||
          current.input?.prompt?.text !== settlement.prompt ||
          textAreaRef?.current?.value !== settlement.prompt
        ) {
          return current
        }
        const clearedLaunch = createNewChatLaunch('agents-chat-recovery-settled', null)
        if (!clearedLaunch.input) return current
        clearedLaunch.input.prompt = {text: ''}
        return clearedLaunch
      })
    })
    return () => cancelAnimationFrame(frame)
  }, [settlement, textAreaRef])

  const hasSpecifiedRepository = launchSpecifiesRepository(launch, searchParams)
  const canSelectChat = isAgentsChatCreationEnabled({
    quickChatEnabled: isFeatureEnabledWithOptOut('one_copilot_chat', 'one_copilot_chat_ui_opt_out'),
    taskDetailEnabled: isFeatureEnabled('copilot_mission_control_agents_task_detail'),
    showSandboxOption: isSandboxOptionAvailable({
      isEnvironmentsEnabled: isFeatureEnabled('copilot_mission_control_environments_web_kickoff'),
      isManagedSandboxEnabled: isFeatureEnabled('copilot_mission_control_managed_sandbox_environments'),
      supportsManagedSandbox: payload.enterpriseSandboxEnabled !== false,
      hasEnvironmentSubmit: true,
    }),
    hasSpecifiedRepository: false,
    scopeLogin: payload.currentUser?.login,
  })
  const allowChat = canSelectChat && !hasSpecifiedRepository

  // A turn composed elsewhere and handed here only needs a way to start it,
  // because the composing prerequisites were already satisfied where it was
  // written. Keeping it on the interactive gate would reject requests this page
  // can plainly start. The environment launcher can run without a Chat API URL.
  const canConsumeChatEntry = isQuickChatEntryPointEnabled({
    quickChatEnabled: isFeatureEnabledWithOptOut('one_copilot_chat', 'one_copilot_chat_ui_opt_out'),
    taskDetailEnabled: isFeatureEnabled('copilot_mission_control_agents_task_detail'),
  })
  // A Chat draft that arrived as a claimed request stays sendable for this
  // mount, so the destination cannot accept the turn and then refuse to send it.
  const allowChatForLaunch = canSelectChat || (launch.fromEntry && launch.input?.target.kind === 'chat')

  // The mode is chosen before the session exists and passed through the
  // composer to the environment launcher for first-turn configuration.
  const independentModeControlsEnabled = isFeatureEnabled('copilot_mission_control_independent_mode_controls')
  const rememberToolPermissionsEnabled = isFeatureEnabled('one_copilot_remember_tool_permissions')
  const [storedMode, setStoredMode] = useState<AgentMode>(() => readStoredMode() ?? DEFAULT_AGENT_MODE)
  const {approvalMode: preferredApprovalMode, selectApprovalMode, setApprovalMode} = useNewSessionApprovalMode()
  const [handoffApprovalMode, setHandoffApprovalMode] = useState<{launchKey: string; mode: ApprovalMode}>()
  if (handoffApprovalMode && handoffApprovalMode.launchKey !== launch.key) setHandoffApprovalMode(undefined)
  const approvalMode =
    rememberToolPermissionsEnabled && handoffApprovalMode?.launchKey === launch.key
      ? handoffApprovalMode.mode
      : preferredApprovalMode
  const selectedMode = storedMode
  const selectedApprovalMode = independentModeControlsEnabled ? approvalMode : undefined
  const onSelectMode = useCallback((nextMode: AgentMode) => {
    setStoredMode(nextMode)
    writeStoredMode(nextMode)
  }, [])
  const onSelectApprovalMode = useCallback(
    (nextMode: ApprovalMode) => {
      if (rememberToolPermissionsEnabled) setHandoffApprovalMode(undefined)
      selectApprovalMode(nextMode)
    },
    [rememberToolPermissionsEnabled, selectApprovalMode],
  )

  const reportEntryFailure = useCallback((message: string) => {
    // Primer's Banner contributes no live semantics, and focus stays in the
    // composer, so the failure is announced rather than only drawn.
    setEntryFailure(message)
    announce(message, {assertive: true})
  }, [])

  const stripEntryParam = useCallback(() => {
    const nextSearchParams = new URLSearchParams(searchParams)
    nextSearchParams.delete(ONE_COPILOT_ENTRY_QUERY_PARAM)
    setSearchParams(nextSearchParams, {replace: true, preventAutofocus: true})
  }, [searchParams, setSearchParams])

  useEffect(() => {
    if (!oneCopilotEntryId || claimedEntryRef.current === oneCopilotEntryId) return
    claimedEntryRef.current = oneCopilotEntryId

    // The marker is single-use, so it is removed whether or not the request can
    // be applied. Leaving it would make an ordinary reload report an expiry.
    stripEntryParam()

    if (!usesOneCopilotComposer) {
      // The composer this page renders cannot carry a prepared prompt, so the
      // record is withdrawn instead of being left to expire behind a draft that
      // silently lost it.
      clearOneCopilotEntry()
      // eslint-disable-next-line react-hooks/set-state-in-effect -- an external, tab-scoped command is being reduced into composer state.
      setLaunch(HOME_LAUNCH)
      reportEntryFailure(ONE_COPILOT_ENTRY_DESTINATION_UNAVAILABLE_MESSAGE)
      return
    }

    const result = claimOneCopilotEntry(oneCopilotEntryId)

    if (result.status !== 'claimed') {
      setLaunch(HOME_LAUNCH)
      reportEntryFailure(ONE_COPILOT_ENTRY_FAILURE_MESSAGES[result.status])
      return
    }

    const input = toInitialInput(result.request)
    const startupApprovalMode = result.request.startupConfiguration?.approvalMode
    if (input.target.kind === 'chat' && !canConsumeChatEntry) {
      // The request has already been consumed, so reporting it is the only way
      // to avoid handing back a Chat draft that can never be sent.
      setLaunch({
        key: `agents-entry-${result.id}-unavailable`,
        pending: false,
        fromEntry: false,
        withholdComposer: true,
      })
      reportEntryFailure(ONE_COPILOT_ENTRY_CHAT_UNAVAILABLE_MESSAGE)
      return
    }

    const claimedLaunchKey = `agents-entry-${result.id}-claimed`
    if (independentModeControlsEnabled && startupApprovalMode) {
      if (rememberToolPermissionsEnabled) {
        setHandoffApprovalMode({launchKey: claimedLaunchKey, mode: startupApprovalMode})
      } else {
        setApprovalMode(startupApprovalMode)
      }
    }
    setEntryFailure(undefined)
    // A key distinct from the pending one guarantees the mount that reads this
    // input is a new mount, because the composer snapshots it once.
    setLaunch({key: claimedLaunchKey, input, pending: false, fromEntry: true})
  }, [
    canConsumeChatEntry,
    independentModeControlsEnabled,
    oneCopilotEntryId,
    rememberToolPermissionsEnabled,
    reportEntryFailure,
    setApprovalMode,
    stripEntryParam,
    usesOneCopilotComposer,
  ])

  useEffect(() => {
    if (!hasNewChatIntent) return

    // eslint-disable-next-line react-hooks/set-state-in-effect -- the URL is an external command that becomes mount-scoped composer state.
    setLaunch(createNewChatLaunch(location.key, searchParams.get('prompt')))
    const updatedSearchParams = new URLSearchParams(searchParams)
    updatedSearchParams.delete('chat')
    setSearchParams(updatedSearchParams, {replace: true, preventAutofocus: true})
  }, [hasNewChatIntent, location.key, searchParams, setSearchParams])

  useEffect(() => {
    const repository = searchParams.get('repo')
    if (
      !usesOneCopilotComposer ||
      !hasNewSessionIntent ||
      !repository ||
      oneCopilotEntryId ||
      launch.key === location.key
    ) {
      return
    }

    const branch = searchParams.get('branch') ?? undefined
    // A sidebar repository action navigates within the already-mounted home
    // route. Remount with the URL's repository so the mount-scoped initial
    // input does not keep showing the previous Chat or repository draft.
    pendingRepositoryFocusKeyRef.current = location.key
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the URL is an external command that becomes mount-scoped composer state.
    setLaunch(createRepositoryLaunch(location.key, repository, branch))
  }, [hasNewSessionIntent, launch.key, location.key, oneCopilotEntryId, searchParams, usesOneCopilotComposer])
  useEffect(() => {
    if (pendingRepositoryFocusKeyRef.current !== launch.key) return
    pendingRepositoryFocusKeyRef.current = undefined
    textAreaRef?.current?.focus()
  }, [launch.key, textAreaRef])

  const dismissEntryFailure = useCallback(() => {
    setEntryFailure(undefined)
    if (launch.withholdComposer) setLaunch(HOME_LAUNCH)
    // Dismissing removes the focused control, so focus is handed back to the
    // composer rather than being left on <body>.
    textAreaRef?.current?.focus()
  }, [launch.withholdComposer, textAreaRef])

  const [handledNewChatIntent, setHandledNewChatIntent] = useState<{presetId: string; wasEligible: boolean}>()
  const onEnvironmentChatSubmissionStarted = useCallback<EnvironmentChatSubmissionHandoff['onSubmissionStarted']>(
    ({prompt, provisioningTarget, initialSelectedSpace, mcpPermissionMode, environmentKind, repository}) =>
      startLiveChat({
        startRequest: createAhpChatStartRequestWithProvisioningTarget({
          prompt,
          target: provisioningTarget,
          ...(initialSelectedSpace
            ? {
                space: {owner: initialSelectedSpace.owner, slug: initialSelectedSpace.slug},
                initialSelectedSpace,
              }
            : {}),
        }),
        payload,
        externallyProvisioned: true,
        environmentKind,
        initialSelectedMode: mapMcpPermissionModeToAgentMode(mcpPermissionMode),
        ...(repository ? {repository} : {}),
      }),
    [payload, startLiveChat],
  )
  const environmentChatSubmissionHandoff = useMemo<EnvironmentChatSubmissionHandoff>(
    () => ({
      onSubmissionStarted: onEnvironmentChatSubmissionStarted,
      onTaskCreated: resolveLiveChatTask,
      onSubmissionFailed: failLiveChat,
    }),
    [failLiveChat, onEnvironmentChatSubmissionStarted, resolveLiveChatTask],
  )
  let currentHandledNewChatIntent = handledNewChatIntent
  if (hasNewChatIntent && handledNewChatIntent?.presetId !== location.key) {
    currentHandledNewChatIntent = {presetId: location.key, wasEligible: allowChat}
    setHandledNewChatIntent(currentHandledNewChatIntent)
  }
  const optimisticChatHandoffEnabled = allowChat && isQuickChatTaskViewEnabled()
  // The created task routes to `/copilot/tasks/:id`, so both task-detail flags must allow that route.
  const repositoryImmediateHandoffEnabled =
    isQuickChatTaskViewEnabled() &&
    isFeatureEnabled('copilot_mission_control_agents_task_detail') &&
    isFeatureEnabled('copilot_mission_control_agents_repository_task_detail')
  const entryAutoSubmitCleanupPending =
    optimisticChatHandoffEnabled &&
    launch.fromEntry &&
    launch.input?.target.kind === 'chat' &&
    launch.input.behavior === 'submit' &&
    readOneCopilotEntryId(location.search) !== null

  return {
    usesOneCopilotComposer,
    composer: {
      earlyProvisioning: allowChatForLaunch ? earlyProvisioning : undefined,
      key: launch.key,
      initialInput: launch.input,
      chatLaunch: allowChatForLaunch
        ? {
            kind: 'environment',
            availability: canSelectChat ? 'selected' : 'implicit',
          }
        : undefined,
    },
    legacy: {
      newChatPresetId:
        allowChat && currentHandledNewChatIntent?.wasEligible ? currentHandledNewChatIntent.presetId : undefined,
      chatLaunch: canSelectChat ? {kind: 'environment'} : undefined,
    },
    isClaimingEntry: launch.pending || entryAutoSubmitCleanupPending,
    isComposerWithheld: launch.pending || entryAutoSubmitCleanupPending || Boolean(launch.withholdComposer),
    mode: {
      ...(selectedMode ? {selectedMode} : {}),
      onSelectMode,
      ...(selectedApprovalMode ? {selectedApprovalMode} : {}),
      onSelectApprovalMode,
    },
    entryFailure,
    dismissEntryFailure,
    submissionFailure,
    recoveryPrompt,
    clearSubmissionFailure,
    chatSubmissionHandoff: optimisticChatHandoffEnabled ? environmentChatSubmissionHandoff : undefined,
    repositorySubmissionHandoff: repositoryImmediateHandoffEnabled ? environmentChatSubmissionHandoff : undefined,
    disableSandboxSubmission,
    chatProviderError,
  }
}
